/* ==========================================================================
   Peinture Nature - runtime
   JavaScript natif, zero framework, zero jQuery. Budget total 30 Ko
   compresse (CDC ch. 14.1). Charge en module differe, jamais bloquant.

   Principe : un composant = un element personnalise. Aucun code global,
   rien qui s'execute si le composant n'est pas present dans la page.
   ========================================================================== */

/* Detail de conception issu du relevé : Booster embarque le gabarit de
   suggestion de recherche dans CHAQUE page, sous la forme d'une chaine
   JavaScript de 14 296 octets contenant 5 cartes produit entierement
   rendues. Tous les visiteurs la telechargent, sur toutes les pages, pour
   un cas d'usage marginal. Ici, rien de tel : les suggestions sont
   demandees au serveur a la premiere interaction. */

const aChargerALaDemande = new Map();

/** Enregistre un composant sans le charger tout de suite. */
export function differer(nom, chemin) {
  aChargerALaDemande.set(nom, chemin);
}

/** Charge un composant a la demande, une seule fois. */
export async function charger(nom) {
  const chemin = aChargerALaDemande.get(nom);
  if (!chemin) return;
  aChargerALaDemande.delete(nom);
  await import(chemin);
}

/* ----- Utilitaire d'ouverture et de fermeture ---------------------------
   Sert au tiroir de panier, au menu mobile et au panneau de filtres.
   Un seul composant, pas un par surface : Booster maintenait deux blocs
   de panier distincts, mobile et desktop, ce qui obligeait a faire chaque
   modification deux fois (contrainte D10). */
export class PnPanneau extends HTMLElement {
  connectedCallback() {
    // Les declencheurs sont branches par une ecoute DELEGUEE sur le document
    // (voir plus bas), et non ici. Raison : `connectedCallback` ne s'execute
    // qu'au moment ou la definition du composant arrive, et ce moment varie
    // selon la facon dont Shopify sert l'asset. Sur telephone, le tiroir ne
    // s'ouvrait pas du tout (releve de Pablo le 2026-07-31) : le lien du
    // panier naviguait vers /cart au lieu d'ouvrir le tiroir. Une ecoute
    // deleguee ne depend d'aucun ordre de chargement.
    this.querySelectorAll('[data-pn-ferme]')
      .forEach((b) => b.addEventListener('click', () => this.fermer()));

    // Fond cliquable. Son absence bloquait le defilement sans issue evidente :
    // le seul moyen de sortir etait la petite croix (releve de Pablo le
    // 2026-07-30). Cree ici et non en Liquid, pour qu'il n'existe pas du tout
    // quand le JavaScript est absent, cas ou le panneau ne s'ouvre jamais.
    this.fond = document.createElement('button');
    this.fond.type = 'button';
    this.fond.className = 'pn-panneau__fond';
    this.fond.hidden = true;
    this.fond.setAttribute('aria-label', 'Fermer');
    this.fond.addEventListener('click', () => this.fermer());
    this.parentNode.insertBefore(this.fond, this);

    this.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); this.fermer(); }
      if (e.key === 'Tab') this.piegerFocus(e);
    });
  }

  get focusables() {
    return [...this.querySelectorAll('a[href], button, input, select, textarea, summary')]
      .filter((e) => !e.disabled && e.offsetParent !== null);
  }

  // Le focus ne doit pas s'echapper derriere un panneau modal.
  piegerFocus(e) {
    const f = this.focusables;
    if (!f.length) return;
    const premier = f[0], dernier = f[f.length - 1];
    if (e.shiftKey && document.activeElement === premier) { e.preventDefault(); dernier.focus(); }
    else if (!e.shiftKey && document.activeElement === dernier) { e.preventDefault(); premier.focus(); }
  }

  ouvrir() {
    this.declencheurPrecedent = document.activeElement;
    this.hidden = false;
    this.fond.hidden = false;
    this.setAttribute('aria-hidden', 'false');
    // Verrou de defilement compatible iOS. `overflow: hidden` sur html NE
    // VERROUILLE PAS le defilement sur Safari iOS : la page continue de
    // bouger derriere le tiroir. On fige donc le body en position fixe, en
    // memorisant la position pour la restituer a la fermeture.
    this.defilementMemorise = window.scrollY;
    const compense = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.defilementMemorise}px`;
    document.body.style.insetInline = '0';
    if (compense > 0) document.body.style.paddingRight = `${compense}px`;
    (this.focusables[0] || this).focus();
  }

  fermer() {
    this.hidden = true;
    this.fond.hidden = true;
    this.setAttribute('aria-hidden', 'true');
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.insetInline = '';
    document.body.style.paddingRight = '';
    if (this.defilementMemorise != null) {
      window.scrollTo(0, this.defilementMemorise);
      this.defilementMemorise = null;
    }
    // On rend le focus a l'element qui a ouvert le panneau.
    if (this.declencheurPrecedent && this.declencheurPrecedent.focus) {
      this.declencheurPrecedent.focus();
    }
  }
}

if (!customElements.get('pn-panneau')) {
  customElements.define('pn-panneau', PnPanneau);
}

/* Ouverture deleguee. Un seul ecouteur pour tous les panneaux, pose sur le
   document : il fonctionne meme si le panneau vise n'est pas encore monte
   au moment du clic, et il survit au remplacement du contenu d'un panneau. */
document.addEventListener('click', (e) => {
  const declencheur = e.target.closest('[data-pn-ouvre]');
  if (!declencheur) return;
  const panneau = document.getElementById(declencheur.dataset.pnOuvre);
  if (!panneau || typeof panneau.ouvrir !== 'function') return;
  // Le declencheur du panier est un LIEN vers /cart, pour que le panier
  // reste atteignable sans JavaScript. Quand le panneau existe, on empeche
  // la navigation et on ouvre le tiroir a la place.
  e.preventDefault();
  panneau.ouvrir();
});

/* Fermeture deleguee, pour la meme raison : le contenu du tiroir est
   remplace a chaque ajout, et les boutons de fermeture avec lui. */
document.addEventListener('click', (e) => {
  const bouton = e.target.closest('[data-pn-ferme]');
  if (!bouton) return;
  const panneau = bouton.closest('pn-panneau');
  if (panneau && typeof panneau.fermer === 'function') panneau.fermer();
});


/* ----- Decalage des elements collants -------------------------------------
   La barre de filtres se cale sous l'en-tete, dont la hauteur depend des
   reglages. On la mesure donc au lieu de la coder en dur.

   PIEGE MAJEUR, rencontre le 2026-07-30 et signale par Pablo.
   La premiere version publiait la mesure dans `--pn-h-entete`. Or cette
   variable sert AUSSI de `min-height` a la rangee de l'en-tete. On avait donc
   une boucle de retroaction : on mesure l'en-tete, la variable grandit, la
   rangee grandit, l'en-tete grandit, on remesure plus grand, et ainsi de
   suite. L'en-tete gonflait sans fin, mesure a 441 puis 487 px au lieu de 118.

   Regle a retenir : une variable ALIMENTEE par une mesure ne doit JAMAIS
   servir a dimensionner l'element mesure. D'ou une variable dediee. */
const enteteCollant = document.querySelector('[id^="shopify-section"]:has(.pn-entete)')
  || document.querySelector('.pn-entete')?.closest('[id^="shopify-section"]');

function publierHauteurEntete() {
  if (!enteteCollant) return;
  // offsetHeight ignore les elements en position absolue, donc les panneaux
  // de mega-menu ouverts ne gonflent pas la mesure.
  const h = enteteCollant.offsetHeight;
  document.documentElement.style.setProperty('--pn-offset-collant', `${h}px`);
}

if (enteteCollant) {
  publierHauteurEntete();
  new ResizeObserver(publierHauteurEntete).observe(enteteCollant);
}

/* HAUTEUR DE LA BARRE DE TRI, meme mecanique et meme raison.
   La colonne de facettes se collait sous le seul en-tete, alors que la barre
   de tri est elle aussi collante et se pose JUSTE EN DESSOUS. La colonne
   glissait donc derriere elle, et ses premieres lignes disparaissaient : sur
   la collection Tournesols filtree, « Paysage » etait coupe en deux par les
   puces de filtres actifs (releve de Pablo le 2026-08-03).
   La barre n'a pas de hauteur fixe, elle depend de son contenu et du nombre
   de puces actives, donc on la mesure au lieu de la deviner. */
const barreCollante = document.querySelector('.pn-barre');

function publierHauteurBarre() {
  if (!barreCollante) return;
  document.documentElement.style.setProperty('--pn-h-barre', `${barreCollante.offsetHeight}px`);
}

if (barreCollante) {
  publierHauteurBarre();
  new ResizeObserver(publierHauteurBarre).observe(barreCollante);
}


/* ----- Bande de recherche -------------------------------------------------
   L'icone ouvre un champ pleine largeur EN PLACE, sans changer de page.
   C'est la reponse au compromis pose dans l'en-tete : la ligne reste sobre
   comme chez Symmetry, sans recreer l'ecart M3 ou la recherche renvoyait
   vers une page. */
const basculeRecherche = document.querySelector('[data-pn-bascule-recherche]');
const bandeRecherche = document.getElementById('pn-recherche');

if (basculeRecherche && bandeRecherche) {
  const champ = bandeRecherche.querySelector('input[type="search"]');

  const ouvrir = () => {
    bandeRecherche.hidden = false;
    basculeRecherche.setAttribute('aria-expanded', 'true');
    champ?.focus();
  };
  const fermer = () => {
    bandeRecherche.hidden = true;
    basculeRecherche.setAttribute('aria-expanded', 'false');
    basculeRecherche.focus();
  };

  basculeRecherche.addEventListener('click', () => {
    bandeRecherche.hidden ? ouvrir() : fermer();
  });
  bandeRecherche.querySelector('[data-pn-ferme-recherche]')?.addEventListener('click', fermer);
  bandeRecherche.addEventListener('keydown', (e) => { if (e.key === 'Escape') fermer(); });
}


/* ==========================================================================
   Panier : ajout sans quitter la page, et tiroir tenu a jour.

   Sur Peinture Nature, l'action d'ajout est reglee sur le tiroir et 78 %
   des clients ne voient JAMAIS la page panier : 687 sessions sur /cart pour
   3 113 ajouts. Le tiroir est donc la surface d'achat principale.

   Tout est en amelioration progressive : sans JavaScript, le formulaire
   d'ajout poste normalement vers /cart/add et les boutons de quantite sont
   des liens vers /cart/change. Rien n'est casse, on perd seulement le
   confort de rester sur la page.
   ========================================================================== */

const tiroir = () => document.querySelector('[data-pn-tiroir-panier]');

// Redemande la section a Shopify et remplace son contenu, sans recharger.
async function rafraichirTiroir(ouvrir = false) {
  const t = tiroir();
  if (!t) return;
  const sectionId = t.closest('[id^="shopify-section-"]')?.id.replace('shopify-section-', '');
  if (!sectionId) return;
  try {
    const html = await (await fetch(`${window.location.pathname}?section_id=${sectionId}`)).text();
    const recu = new DOMParser().parseFromString(html, 'text/html')
      .querySelector('[data-pn-tiroir-panier]');
    if (!recu) return;
    const etaitOuvert = !t.hidden;
    t.innerHTML = recu.innerHTML;
    if (ouvrir || etaitOuvert) t.ouvrir();
  } catch (e) { /* on laisse le repli serveur */ }
  majCompteur();
}

async function majCompteur() {
  try {
    const c = await (await fetch('/cart.js')).json();
    document.querySelectorAll('[data-pn-compteur-panier]')
      .forEach((el) => { el.textContent = c.item_count; });
  } catch (e) { /* sans importance */ }
}

// Ajout au panier depuis la fiche produit.
document.addEventListener('submit', async (e) => {
  const form = e.target.closest('[data-pn-form-panier]');
  if (!form || !tiroir()) return;
  e.preventDefault();
  const bouton = form.querySelector('[data-pn-ajouter]');
  const libelle = bouton?.textContent;
  if (bouton) { bouton.disabled = true; bouton.textContent = '…'; }
  try {
    const r = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: form.querySelector('[data-pn-id-variante]').value, quantity: 1 }),
    });
    if (!r.ok) throw new Error(r.status);
    await rafraichirTiroir(true);
  } catch (err) {
    form.submit(); // repli : on laisse Shopify faire, page panier classique
  } finally {
    if (bouton) { bouton.disabled = false; bouton.textContent = libelle; }
  }
});

// Boutons de quantite a l'interieur du tiroir.
document.addEventListener('click', async (e) => {
  const lien = e.target.closest('[data-pn-maj-quantite]');
  if (!lien) return;
  e.preventDefault();
  await fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ line: Number(lien.dataset.pnMajQuantite),
                           quantity: Number(lien.dataset.pnQuantite) }),
  });
  await rafraichirTiroir();
});


/* ==========================================================================
   Menu de navigation : ouverture au SURVOL en bureau.

   Demande de Pablo le 2026-07-31 : « moins il y a de clics, plus le
   parcours d'achat est fluide ». Le menu reste bati sur `details` et
   `summary`, donc il s'ouvre toujours au clic et au clavier, et il
   fonctionne sans JavaScript. Le survol ne fait qu'ajouter un raccourci.

   Deux precautions :
     - uniquement sur les appareils a vrai pointeur. Au doigt, un survol
       s'active au premier contact et le menu s'ouvrirait tout seul.
     - un delai court a la fermeture, sinon le menu se referme des qu'on
       traverse un blanc entre l'intitule et le panneau.
   ========================================================================== */
if (window.matchMedia('(hover: hover) and (min-width: 990px)').matches) {
  const tiroirs = document.querySelectorAll('.pn-nav__tiroir');
  let fermeture;

  const ouvrir = (t) => {
    clearTimeout(fermeture);
    tiroirs.forEach((autre) => { if (autre !== t) autre.open = false; });
    t.open = true;
  };
  const programmerFermeture = () => {
    clearTimeout(fermeture);
    fermeture = setTimeout(() => tiroirs.forEach((t) => { t.open = false; }), 180);
  };

  tiroirs.forEach((t) => {
    t.addEventListener('mouseenter', () => ouvrir(t));
    t.addEventListener('mouseleave', programmerFermeture);
    // Le panneau deborde du `details` : on le retient aussi.
    t.querySelector('.pn-nav__panneau')?.addEventListener('mouseenter', () => clearTimeout(fermeture));
    // Au clic, `details` bascule tout seul : on evite qu'un clic sur un
    // intitule deja ouvert par le survol le referme aussitot.
    t.querySelector('.pn-nav__declencheur')?.addEventListener('click', (e) => {
      // L'intitule est desormais un LIEN vers la collection : un clic dessus
      // doit naviguer, pas replier le panneau. Sans cette exception, le
      // `preventDefault` ci-dessous annulait aussi la navigation, et le menu
      // redevenait un simple libelle en bureau.
      if (e.target.closest('.pn-nav__parent')) return;
      if (t.open) e.preventDefault();
    });
  });

  // Sortie par le haut ou par le bas de la barre : on ferme.
  document.querySelector('.pn-entete__nav')?.addEventListener('mouseleave', programmerFermeture);
}

/* ==========================================================================
   Menu en telephone.

   La navigation etait masquee sous 990 px et aucun bouton ne l'ouvrait : la
   boutique n'avait AUCUN menu sur telephone, ou passe pourtant l'essentiel
   du trafic. Releve de Pablo le 2026-08-01.

   Le tiroir sert le MEME balisage que le menu de bureau. Il se ferme au
   voile, a la croix, a la touche Echap et des qu'on suit un lien.
   ========================================================================== */
(() => {
  const bouton = document.querySelector('[data-pn-bascule-menu]');
  const menu = document.getElementById('pn-menu-mobile');
  const voile = document.querySelector('[data-pn-voile-menu]');
  if (!bouton || !menu) return;

  const ouvrir = () => {
    menu.setAttribute('data-ouvert', '');
    if (voile) voile.hidden = false;
    bouton.setAttribute('aria-expanded', 'true');
    document.documentElement.style.overflow = 'hidden';
    menu.querySelector('a, button')?.focus();
  };
  const fermer = () => {
    menu.removeAttribute('data-ouvert');
    if (voile) voile.hidden = true;
    bouton.setAttribute('aria-expanded', 'false');
    document.documentElement.style.overflow = '';
  };

  bouton.addEventListener('click', () => {
    menu.hasAttribute('data-ouvert') ? fermer() : ouvrir();
  });
  voile?.addEventListener('click', fermer);
  menu.querySelector('[data-pn-ferme-menu]')?.addEventListener('click', fermer);
  // Suivre un lien ferme le tiroir : sans ca, le retour arriere du navigateur
  // ramene sur une page dont le menu est reste ouvert.
  menu.addEventListener('click', (e) => { if (e.target.closest('a')) fermer(); });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && menu.hasAttribute('data-ouvert')) { fermer(); bouton.focus(); }
  });
  // Passage en bureau : on remet tout a plat, sinon le defilement reste bloque.
  matchMedia('(min-width: 990px)').addEventListener('change', (e) => { if (e.matches) fermer(); });
})();
